/**
 * NotificationSettings - Configuracion de notificaciones push
 *
 * Diseñado para neurodivergencia:
 * - Control total del usuario
 * - Explicaciones claras de cada opcion
 * - Sin presion para activar
 */

import { useState, useEffect } from 'react';
import { Bell, BellOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/ui/primitives';
import { cn } from '@/core/utils';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  sendReminder,
} from '@/services/notifications';
import { showSuccess, showError, showInfo } from '@/store';
import { feedbackSuccess } from '@/services/feedback';

type PermissionState = 'loading' | 'unsupported' | 'default' | 'granted' | 'denied';

export function NotificationSettings() {
  const [permissionState, setPermissionState] = useState<PermissionState>('loading');
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    if (!isNotificationSupported()) {
      setPermissionState('unsupported');
      return;
    }

    const permission = getNotificationPermission();
    setPermissionState(permission);
  }, []);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const result = await requestNotificationPermission();
      setPermissionState(result);

      if (result === 'granted') {
        feedbackSuccess();
        showSuccess('Notificaciones activadas');
        // Enviar notificacion de prueba
        setTimeout(() => {
          sendReminder('morning_start');
        }, 1000);
      } else if (result === 'denied') {
        showInfo('Puedes activar las notificaciones desde la configuracion del navegador');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      showError('Error al solicitar permiso');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleTestNotification = async () => {
    await sendReminder('protocol_reminder');
    showInfo('Notificacion de prueba enviada');
  };

  // UI segun estado
  if (permissionState === 'loading') {
    return (
      <div className="p-3 rounded-lg bg-slate-800/30 animate-pulse">
        <div className="h-4 bg-slate-700 rounded w-3/4" />
      </div>
    );
  }

  if (permissionState === 'unsupported') {
    return (
      <div className="p-3 rounded-lg bg-slate-800/30">
        <div className="flex items-start gap-3">
          <BellOff className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-400">
              Las notificaciones no estan soportadas en este navegador.
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Prueba con Chrome, Firefox o Edge para esta funcion.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section>
      <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
        <Bell className="w-4 h-4" />
        Notificaciones
      </h3>

      <div className="space-y-3">
        {/* Estado actual */}
        <div
          className={cn(
            'p-3 rounded-lg flex items-start gap-3',
            permissionState === 'granted'
              ? 'bg-emerald-900/20 border border-emerald-800/50'
              : permissionState === 'denied'
              ? 'bg-rose-900/20 border border-rose-800/50'
              : 'bg-slate-800/30'
          )}
        >
          {permissionState === 'granted' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          ) : permissionState === 'denied' ? (
            <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
          ) : (
            <Bell className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          )}

          <div className="flex-1">
            {permissionState === 'granted' && (
              <>
                <p className="text-sm text-emerald-300">
                  Notificaciones activadas
                </p>
                <p className="text-xs text-emerald-400/70 mt-1">
                  Recibiras recordatorios gentiles para tus protocolos.
                </p>
              </>
            )}

            {permissionState === 'denied' && (
              <>
                <p className="text-sm text-rose-300">
                  Notificaciones bloqueadas
                </p>
                <p className="text-xs text-rose-400/70 mt-1">
                  Puedes habilitarlas en la configuracion de tu navegador.
                </p>
              </>
            )}

            {permissionState === 'default' && (
              <>
                <p className="text-sm text-slate-300">
                  Notificaciones desactivadas
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Activalas para recibir recordatorios amables sobre tus protocolos.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Boton de accion */}
        {permissionState === 'default' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRequestPermission}
            disabled={isRequesting}
            className="w-full"
          >
            <Bell className="w-4 h-4 mr-2" />
            {isRequesting ? 'Solicitando...' : 'Activar notificaciones'}
          </Button>
        )}

        {permissionState === 'granted' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleTestNotification}
            className="w-full text-slate-400"
          >
            Enviar notificacion de prueba
          </Button>
        )}

        {/* Nota importante */}
        <p className="text-2xs text-slate-600">
          Las notificaciones son opcionales y siempre respetuosas.
          Nunca te presionaremos ni te haremos sentir culpable.
        </p>
      </div>
    </section>
  );
}
